// Composer hook that wires the domain hooks together. App.tsx calls this once
// and renders against the returned slice; future step splits the JSX into
// view components that read from the same slice.

import React, { useEffect, useMemo, useState } from "react";
import { createIdpcSource } from "../data/idpc";
import { downloadCsv } from "../lib/csvExport";
import { buildApiHeaders, buildOutputHeaders } from "../lib/cleanseSchema";
import { useMode } from "./useMode";
import { useColumnMapping } from "./useColumnMapping";
import { useCsv } from "./useCsv";
import { useApiKey } from "./useApiKey";
import { useRecovery } from "./useRecovery";
import { useProcessing } from "./useProcessing";

const BASE_URL = "https://api.ideal-postcodes.co.uk";
const VALIDATION_DEBOUNCE_MS = 1000;

export type AppState = ReturnType<typeof useCleanseApp>;

export function useCleanseApp() {
  const [error, setError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const { mode, setMode } = useMode();
  const mapping = useColumnMapping();
  const csv = useCsv({ onError: setError });
  const apiKeyState = useApiKey();
  const recovery = useRecovery();

  const {
    apiKey,
    setApiKeyValid,
    setValidating: setValidatingApiKey,
  } = apiKeyState;

  const dataSource = useMemo(
    () => createIdpcSource({ baseUrl: BASE_URL, apiKey }),
    [apiKey]
  );

  const proc = useProcessing({
    dataSource,
    setError,
    saveSnapshot: recovery.save,
    clearSnapshot: recovery.clear,
  });

  // Debounced API key validation. Lives at the composer because dataSource
  // depends on apiKey, which would otherwise create a circular dependency
  // between useApiKey and useDataSource.
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (!apiKey.trim()) {
        setApiKeyValid(null);
        return;
      }
      setValidatingApiKey(true);
      setApiKeyValid(null);
      try {
        const result = await dataSource.validateKey();
        setApiKeyValid(result.available);
        if (result.available) setError(null);
      } catch (e) {
        console.error("API key validation error:", e);
        setApiKeyValid(false);
      } finally {
        setValidatingApiKey(false);
      }
    }, VALIDATION_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [apiKey, dataSource, setApiKeyValid, setValidatingApiKey]);

  const canProcess = useMemo(() => {
    if (
      !apiKeyState.apiKey ||
      apiKeyState.apiKeyValid !== true ||
      csv.rows.length === 0
    )
      return false;
    return mode === "cleanse"
      ? !!mapping.line1Col && !!mapping.postcodeCol
      : !!mapping.uprnCol;
  }, [
    apiKeyState.apiKey,
    apiKeyState.apiKeyValid,
    csv.rows.length,
    mapping.line1Col,
    mapping.postcodeCol,
    mapping.uprnCol,
    mode,
  ]);

  const apiHeaders = useMemo(() => buildApiHeaders(mode), [mode]);
  const outputHeaders = useMemo(
    () => buildOutputHeaders(csv.csvHeaders, apiHeaders),
    [csv.csvHeaders, apiHeaders]
  );

  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      action();
    }
  };

  async function handleFile(file?: File | null) {
    setError(null);
    const result = await csv.loadFile(file);
    if (!result) return;
    mapping.autoDetect(result.headers, mode);
    proc.reset();
    recovery.clear();
  }

  const resumeFromCrash = () => {
    const snap = recovery.load();
    if (!snap) return;
    csv.setSnapshot(snap.csvHeaders, snap.originalRows);
    mapping.setLine1Col(snap.line1Col);
    mapping.setPostcodeCol(snap.postcodeCol);
    proc.restore(snap);
    recovery.setHasRecoveryData(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    csv.setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    csv.setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    csv.setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) handleFile(files[0]);
  };

  async function processAll() {
    if (!canProcess) return;
    const snap = recovery.load();
    const input = {
      rows: csv.rows,
      csvHeaders: csv.csvHeaders,
      mode,
      line1Col: mapping.line1Col,
      postcodeCol: mapping.postcodeCol,
      uprnCol: mapping.uprnCol,
    };
    if (snap && snap.progress.done < snap.progress.total) {
      await proc.start(input, {
        startIndex: snap.progress.done,
        existingResults: snap.resultRows,
      });
    } else {
      await proc.start(input);
    }
  }

  function handleDownload() {
    if (!proc.resultRows.length) return;
    const baseFileName = csv.uploadedFileName.replace(/\.csv$/i, "");
    const isPartial = proc.resultRows.length < csv.rows.length;
    const suffix = isPartial ? "_partial_cleansed" : "_cleansed";
    downloadCsv(`${baseFileName}${suffix}.csv`, outputHeaders, proc.resultRows);
  }

  const startNewCleanse = () => setShowConfirmDialog(true);

  const handleConfirmNewCleanse = (confirmed: boolean) => {
    setShowConfirmDialog(false);
    if (!confirmed) return;
    csv.reset();
    mapping.reset();
    proc.reset();
    setError(null);
    recovery.clear();
    // API key intact — user typically reuses the same key for new cleanses.
  };

  return {
    // mode
    mode,
    setMode,
    // mapping
    mapping,
    // csv
    csv,
    // api key
    apiKey: apiKeyState.apiKey,
    setApiKey: apiKeyState.setApiKey,
    apiKeyValid: apiKeyState.apiKeyValid,
    validatingApiKey: apiKeyState.validating,
    // processing
    proc,
    // recovery
    recovery,
    // app-level state
    error,
    showConfirmDialog,
    canProcess,
    outputHeaders,
    // orchestrators
    handleKeyDown,
    handleFile,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    resumeFromCrash,
    processAll,
    handleDownload,
    startNewCleanse,
    handleConfirmNewCleanse,
  };
}
